<template>
  <div class="gap-size-24 flex shrink-0 items-center">
    <BaseChip
      v-if="isDemoMode()"
      class="!text-font-12 shrink-0 whitespace-nowrap !font-semibold"
      label="모의계좌 접속중"
      textColor="var(--font-color-default)"
      backgroundColor="var(--background-bg-default)"
      size="md"
    />
    <div>
      <!-- 로딩 중일 때 스켈레톤 UI -->
      <div class="w-[260px]" v-if="isLoadingAccounts"></div>
      <!-- 로딩 완료 후 계좌 목록이 없을 때 -->
      <div class="w-[145px]" v-else-if="accountList.length === 0">
        <BaseButton
          variant="outlined"
          label="계좌 개설하기"
          size="md"
          color="yellow"
          fullWidth
          :leftIcon="{ name: 'plus', color: 'primary' }"
          @click="handleOpenAccountCreate"
        />
      </div>
      <!-- 계좌 목록이 있을 때 -->
      <div class="w-[260px]" v-else>
        <BaseInputSelect
          :modelValue="accountStore.selectedAccountNo"
          color="black"
          :options="accountList"
          @open="getAccountList"
          @update:modelValue="handleAccountChange"
        />
      </div>
    </div>
    <div class="gap-size-48 flex shrink-0 items-center">
      <dl
        v-for="metric in financialMetrics"
        :key="metric.id"
        class="flex min-w-[60px] flex-col items-start gap-1"
      >
        <dt class="text-default-muted-light tracking-0 text-[12px]">{{ metric.label }}</dt>
        <dd class="tracking-3 font-semibold text-white">{{ metric.value }}</dd>
      </dl>
    </div>
    <div class="h-size-20 shrink-0 border-bg-divider-muted border" />
    <div class="flex shrink-0 items-center gap-4">
      <BaseMenu
        :items="menuItems"
        align="left"
        :menu-width="180"
        items-class="status-bar-area-menu"
        @select="handleMenuSelect"
      >
        <template v-if="isDemoMode()" #prepend>
          <StatusBarCustomerVerificationDocsButton @click="isVisible = true" />
        </template>

        <template #trigger>
          <BaseIconButton
            color="var(--input-icon-default)"
            :icon="{ name: 'person' }"
            backgroundColor="var(--background-bg-surface-muted)"
          />
        </template>
      </BaseMenu>
      <BaseIconButton
        color="white"
        :icon="!isDark ? { name: 'mode-dark' } : { name: 'mode-light' }"
        @click="handleThemeToggle"
      />
      <BasePopover position="bottom-right">
        <template #trigger>
          <BaseIconButton
            color="white"
            :icon="{ name: 'notification' }"
            :badge="{ value: 0, variant: 'dot', hidden: true }"
          />
        </template>
        <template #content="{ close }">
          <NotificationPopoverContent :close="close" />
        </template>
      </BasePopover>
    </div>
  </div>
  <RequiredDocumentsDialog
    :is-visible="isVisible"
    :type="userType === 'INDIVIDUAL' ? 'individual' : 'corporation'"
    @close="isVisible = false"
    @confirm="handleClick"
  />
</template>

<script setup lang="ts">
import {
  useTheme,
  BaseMenu,
  BaseInputSelect,
  BaseButton,
  BasePopover,
  BaseIconButton,
  BaseChip,
} from '@template/ui';
import StatusBarCustomerVerificationDocsButton from './StatusBarCustomerVerificationDocsButton.vue';
import RequiredDocumentsDialog from '@/components/auth/signup/dialog/RequiredDocumentsDialog.vue';
import { getGlobalChartSettingsInstance } from '@/composables/useGlobalChartSettings';
import { useGlobalDialogManager } from '@/composables/useGlobalDialogManager';
import NotificationPopoverContent from './NotificationPopoverContent.vue';
import { LocalStorageService, LocalStorageKey } from '@template/utils';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { usePositionData } from '@/composables/usePositionData';
import { useAccountStore } from '@template/stores';
import { Logout } from '@/services/auth.service';
import { isDemoMode } from '@template/utils';

import { formatPriceFloor, formatPercentage } from '@template/utils';
import { useOrderDataStore } from '@template/stores';
import { useAssetStore } from '@template/stores';
import { accountService } from '@/services/api';
import { useRouter } from 'vue-router';

const isVisible = ref(false);
const userType = LocalStorageService.getItem(LocalStorageKey.MEMBER_TYPE);

const { isDark, toggleTheme } = useTheme();
const globalChartSettings = getGlobalChartSettingsInstance();

// 테마 토글 시 차트 배경도 함께 업데이트
const handleThemeToggle = () => {
  toggleTheme();

  // 테마 변경 후 약간의 지연을 두고 차트 배경 업데이트
  // CSS 변수가 완전히 적용된 후 실행
  setTimeout(() => {
    globalChartSettings.updateAllChartsThemeBackground();
  }, 50);
};
const accountStore = useAccountStore();
const assetStore = useAssetStore();
const orderDataStore = useOrderDataStore();
const positionData = usePositionData();
const isLoadingAccounts = ref(false);

const totalProfitLoss = computed(() => orderDataStore.totalProfitLoss);

const accountList = computed(() =>
  [...accountStore.availableAccountListSortedComputed].map((account) => ({
    value: account.accountNo,
    label: `${account.accountAlias}#${account.accountSequence} ${account.accountNo}`,
  }))
);

/**
 * 달러 금액을 포맷팅하는 함수
 * 음수일 때는 -$12.34 형식으로 표시
 * @param amount - 포맷팅할 금액
 * @returns 포맷팅된 금액 문자열
 */
const formatCurrency = (amount: number): string => {
  if (amount === 0) return '-';
  return amount < 0
    ? `-$${formatPriceFloor(Math.abs(amount), 0)}`
    : `$${formatPriceFloor(amount, 0)}`;
};

/**
 * - 예탁자산 = 예수금
 * - 증거금 = 증거금 총액
 * - P/L = 종목별 P/L 합계
 *
 * - 자산총액 = 예탁자산 + P/L
 *
 * - 증거금율 = 자산총액 / 증거금 * 100
 */
const financialMetrics = computed(() => [
  {
    id: 'total-assets',
    label: '자산총액',
    value: formatCurrency(assetStore.totalAssets), // 예탁자산 + P/L
  },
  {
    id: 'deposit-balance',
    label: '예탁자산',
    value: formatCurrency(assetStore.depositBalanceComputed), // 예수금
  },
  {
    id: 'profit-loss',
    label: 'P/L',
    value: formatCurrency(totalProfitLoss.value), // 잔고 탭의 종목별 P/L의 합계
  },
  {
    id: 'margin',
    label: '증거금',
    value: formatCurrency(assetStore.marginBalanceComputed), // 증거금 총액
  },
  {
    id: 'margin-rate',
    label: '증거금율',
    value:
      assetStore.marginBalanceComputed === 0
        ? '-'
        : `${formatPercentage(
            ((assetStore.depositBalanceComputed + totalProfitLoss.value) /
              assetStore.marginBalanceComputed) *
              100
          )}%`, // 자산총액 / 증거금 × 100
  },
]);

const router = useRouter();
const dialogManager = useGlobalDialogManager();

const menuItems = computed(() => {
  const items = [
    { label: '계좌 관리', value: 'account-management' },
    { label: '마이페이지', value: 'mypage' },
    { label: '개인정보 설정', value: 'member-info-edit' },
    { label: '로그아웃', value: 'logout' },
  ];
  if (isDemoMode()) {
    return items.filter((item) => item.value !== 'member-info-edit');
  }
  return items;
});

/**
 * 예탁자산(예수금/증거금총액)을 조회하여 스토어에 반영합니다.
 * @returns Promise<void>
 */
const loadDepositAsset = async (): Promise<void> => {
  if (!accountStore.selectedAccountNo) return;

  try {
    const res = await accountService.getDepositAsset(accountStore.selectedAccountNoSafe);
    const data = res.data;
    if (data) {
      const deposit = data.depositAmount ?? 0;
      const margin = data.marginTotalAmount ?? 0;
      assetStore.updateBalance(deposit, margin);
    }
  } catch (error) {
    console.error('예탁자산 조회 실패:', error);
  }
};

/**
 * 계좌 변경 핸들러
 * selectAccount가 완료될 때까지 기다립니다 (동기적으로 수행)
 */
const handleAccountChange = async (accountNo: string | undefined) => {
  if (accountNo === accountStore.selectedAccountNo) return;
  try {
    await accountStore.selectAccount(accountNo);
    accountStore.setAccountSimpleAuthEnabled(false);
    // 계좌 변경 시 1회 노출: 팝업을 띄우면 스토리지 제거
    LocalStorageService.removeItem(LocalStorageKey.ACCOUNT_PASSWORD_POPUP_NEEDED);
    dialogManager.open('accountPasswordSetup', async (payload) => {
      try {
        if (payload.quickAuthEnabled && payload.password) {
          await accountService.checkPassword({
            accountNo: accountStore.selectedAccountNoSafe,
            accountPassword: payload.password,
            quickAuthEnabled: payload.quickAuthEnabled,
          });
          accountStore.setAccountSimpleAuthEnabled(true);
        } else {
          accountStore.setAccountSimpleAuthEnabled(false);
        }

        dialogManager.close('accountPasswordSetup');
      } catch (error) {
        console.error('계좌 비밀번호 저장 실패:', error);
      }
    });
  } catch (error) {
    console.error('계좌 변경 실패:', error);
  }
};

const handleMenuSelect = (item: any) => {
  switch (item.value) {
    case 'logout':
      Logout();
      break;
    case 'mypage':
      router.push({ name: 'mypage', params: { menuTab: 'login-history' } });
      break;
    case 'member-info-edit':
      dialogManager.open('memberInfoEdit');
      break;
    default:
      router.push({ name: item.value });
      break;
  }
};

/**
 * 계좌 개설하기 버튼 클릭 시 계좌관리 > 계좌개설 화면으로 이동하고 새 계좌 개설 UI 표시
 */
const handleOpenAccountCreate = () => {
  router.push({
    name: 'account-management',
    params: { accountManagementTab: 'create' },
    query: { state: 'new' },
  });
};

/**
 * 계좌 목록을 조회합니다.
 */
const getAccountList = async () => {
  // 기존 데이터가 있으면 로딩 상태를 표시하지 않음
  if (accountStore.accountListComputed.length === 0) {
    isLoadingAccounts.value = true;
  }

  try {
    const result = await accountService.getAccountInfo();
    const accountList = result.data?.accountList || [];

    // StatusBarArea에서는 계좌 목록만 업데이트
    await accountStore.setAccountList(accountList);

    // 선택된 계좌가 있으면 예탁자산 조회 API 호출
    if (accountStore.selectedAccountNo) {
      await loadDepositAsset();
    }
  } catch (error) {
    console.error('계좌 목록 조회 실패:', error);
  } finally {
    isLoadingAccounts.value = false;
  }
};

// 계좌 변경 시 해당 계좌의 예탁자산(예수금/증거금총액) 업데이트
watch(
  () => accountStore.selectedAccountNo,
  async (newAccountNo) => {
    if (!newAccountNo) return;

    // 잔고 데이터 다시 로드
    await positionData.loadPositionData();

    await loadDepositAsset();
  },
  { immediate: true }
);

/** 웹소켓 재연결/계좌 등급 변경 시 잔고·예탁자산 풀싱크 */
const handleAccountGradeChangedForPositionSync = async (event: Event) => {
  const customEvent = event as CustomEvent<{ accountNo: string }>;
  const accountNo = customEvent.detail?.accountNo;
  if (!accountNo || accountNo !== accountStore.selectedAccountNo) return;
  try {
    await positionData.loadPositionData();
    await loadDepositAsset();
  } catch (error) {
    console.error('[StatusBarArea] 잔고/예탁자산 풀싱크 실패:', error);
  }
};

const handleClick = () => {
  const userType = LocalStorageService.getItem(LocalStorageKey.MEMBER_TYPE);
  if (userType === 'INDIVIDUAL') {
    router.push({ name: 'individual-document' });
  } else {
    router.push({ name: 'corporate-document' });
  }
};

onMounted(() => {
  if (accountStore.accountListComputed.length === 0) {
    getAccountList();
  }
  window.addEventListener(
    'accountGradeChanged',
    handleAccountGradeChangedForPositionSync as EventListener
  );
});

onUnmounted(() => {
  window.removeEventListener(
    'accountGradeChanged',
    handleAccountGradeChangedForPositionSync as EventListener
  );
});
</script>

<style>
/* StatusBarArea 전용: BaseMenu 마지막 아이템 위 경계선 (Teleport된 메뉴는 scoped 미적용) */
.status-bar-area-menu .base-menu__item:last-child {
  border-top: 1px solid var(--background-bg-outline);
}
</style>
